import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

// ══════════════════════════════════════════════════════════════════════════════
// POR QUE A IA ESTÁ NESTA CONVERSA — a palavra que aparece no cartão
// ══════════════════════════════════════════════════════════════════════════════
//
// 🔴 POR QUE ISTO É UM COMPOSABLE (Fase 6 do Lumos, 18/09/2026)
//
// O produto tem DOIS cartões de conversa — o clássico
// (`components/widgets/conversation/ConversationCard.vue`, o que a lista usa
// hoje) e o novo (`components-next/.../ConversationCard.vue`). Escrever a mesma
// lógica nos dois seria repetir, em pequeno, exatamente o defeito que a Fase 6
// conserta: o mesmo fato decidido em vários lugares, divergindo com o tempo.
//
// A DECISÃO não mora aqui. Ela é do servidor (`Lumos::FilaDaIa`), que carimba
// cada conversa da lista com `motivo_da_ia` — o MESMO cálculo que decide quem
// entra na aba da IA. Aqui só se escolhe a palavra e a cor.
//
// Formato do carimbo:
//   { quem: 'triagem' | 'follow_up' | 'cobranca' | 'bot',
//     detalhe: { ... } }   ← campos diferentes por origem
export function useMotivoDaIa(conversa) {
  const { t } = useI18n();

  // `toValue` e não `unref`: os dois cartões passam um GETTER
  // (`() => props.chat`), e `unref` devolveria a própria função — o motivo
  // ficava sempre nulo e a tarja nunca aparecia. Visto no localhost.
  const motivo = computed(() => toValue(conversa)?.motivo_da_ia || null);

  // Âmbar é "parado, esperando uma pessoa"; teal é "trabalhando agora".
  // A cobrança à espera de aprovação é a única que pede alguém do escritório —
  // e por isso é a única que muda de tom. Cor só onde carrega significado.
  const esperandoPessoa = computed(
    () =>
      motivo.value?.quem === 'cobranca' &&
      motivo.value?.detalhe?.estado === 'aguardando_aprovacao'
  );

  const tom = computed(() => (esperandoPessoa.value ? 'ambar' : 'teal'));

  // Dentro de chip pequeno o ícone é de TRAÇO (`i-lucide-*`, 1,8px): massa
  // sólida em 10px vira borrão. Ver `.claude/rules/design-system.md`.
  const ICONE = {
    triagem: 'i-lucide-message-circle-more',
    follow_up: 'i-lucide-repeat-2',
    cobranca: 'i-lucide-receipt-text',
    bot: 'i-lucide-sparkles',
  };
  const icone = computed(
    () => ICONE[motivo.value?.quem] || 'i-lucide-sparkles'
  );

  const passos = () => ({
    passo: motivo.value?.detalhe?.passo ?? '?',
    de: motivo.value?.detalhe?.de ?? '?',
  });

  // A etiqueta curta cabe na lista estreita sem empurrar nada.
  const curto = computed(() => {
    const m = motivo.value;
    if (!m) return '';
    if (m.quem === 'follow_up') {
      return t('CHAT_LIST.IA.FOLLOW_UP_CURTO', passos());
    }
    return t(`CHAT_LIST.IA.${m.quem.toUpperCase()}_CURTO`);
  });

  // A frase inteira vai para a legenda, onde há espaço para ela.
  const completo = computed(() => {
    const m = motivo.value;
    if (!m) return '';
    if (m.quem === 'triagem') {
      return m.detalhe?.assistente
        ? t('CHAT_LIST.IA.TRIAGEM', { assistente: m.detalhe.assistente })
        : t('CHAT_LIST.IA.TRIAGEM_SEM_NOME');
    }
    if (m.quem === 'follow_up') return t('CHAT_LIST.IA.FOLLOW_UP', passos());
    if (m.quem === 'cobranca') {
      return esperandoPessoa.value
        ? t('CHAT_LIST.IA.COBRANCA_APROVAR')
        : t('CHAT_LIST.IA.COBRANCA');
    }
    return t('CHAT_LIST.IA.BOT');
  });

  return { motivo, esperandoPessoa, tom, icone, curto, completo };
}
